/**
 * Smart Editor Context Module
 * Provides context for managing smart editor state
 *
 * @module SmartEditorContext
 */

import { createContext, MutableRefObject, ReactNode, useContext } from 'react';
import { ISmartEditorStore } from '..';

/**
 * Context for sharing smart editor store reference
 * Uses MutableRefObject to maintain reference stability
 *
 * @type {React.Context<MutableRefObject<ISmartEditorStore | undefined> | null>}
 */
export const SmartEditorContext = createContext<MutableRefObject<
  ISmartEditorStore | undefined
> | null>(null);

/**
 * Props for SmartEditorProvider component
 *
 * @interface SmartEditorProviderProps
 * @property {ReactNode} children - Child components
 * @property {MutableRefObject<ISmartEditorStore | undefined>} store - Reference to editor store
 */
interface SmartEditorProviderProps {
  children: ReactNode;
  store: MutableRefObject<ISmartEditorStore | undefined>;
}

/**
 * Provider component for smart editor context
 * Manages store reference and validation
 *
 * @component
 * @param {SmartEditorProviderProps} props - Component props
 * @param {ReactNode} props.children - Child components
 * @param {MutableRefObject<ISmartEditorStore>} props.store - Store reference
 *
 * @example
 * const storeRef = useRef<ISmartEditorStore>();
 * <SmartEditorProvider store={storeRef}>
 *   <Editor />
 * </SmartEditorProvider>
 *
 * @throws {Error} When store prop is missing
 */
export const SmartEditorProvider = ({ children, store }: SmartEditorProviderProps) => {
  if (!store) {
    throw new Error('SmartEditorProvider requires a store prop');
  }
  return <SmartEditorContext.Provider value={store}>{children}</SmartEditorContext.Provider>;
};

/**
 * Hook for accessing smart editor store
 * Must be used within SmartEditorProvider
 *
 * @hook
 * @returns {ISmartEditorStore} Smart editor store instance
 * @throws {Error} When used outside of SmartEditorProvider or store is undefined
 *
 * @example
 * const store = useSmartEditor();
 * const { content, isEditing } = store;
 */
export const useSmartEditor = () => {
  const storeRef = useContext(SmartEditorContext);
  if (!storeRef || !storeRef.current) {
    throw new Error('useSmartEditor must be used within a SmartEditorProvider');
  }
  return storeRef.current;
};
